import { useLocale } from '@va/localization';
import { LatLngBoundsLiteral, Map } from 'leaflet';
import { useCallback, useRef } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import CustomMarkerClusterGroup from './CustomMarkerClusterGroup';
import './index.scss';
import { LeafletMapPoint } from './types';

type LeafletMapProps = {
  circleRadius?: number;
  points: LeafletMapPoint[];
  center?: {
    latitude: number;
    longitude: number;
  };
  zoom?: number;
  whenCreated?: (map: Map) => void;
};

const MAX_ZOOM = 14;
const MIN_ZOOM = 1;
const MAX_BOUNDS: LatLngBoundsLiteral = [
  [85, -180],
  [-85, 180],
];

/**
 * Leaflet map with clustering and async tooltip
 */
const LeafletMap = ({
  center = { latitude: 45, longitude: -45 },
  points = [],
  zoom = 3,
  circleRadius = 2500,
  whenCreated,
}: LeafletMapProps) => {
  const { locale } = useLocale();

  // react-leaflet 4+ dropped `whenCreated`; the map instance now arrives through the ref.
  // The latest callback is kept in a ref so the stable ref callback only fires when the map is created.
  const whenCreatedRef = useRef(whenCreated);
  whenCreatedRef.current = whenCreated;
  const mapRef = useCallback((map: Map | null) => {
    if (map) whenCreatedRef.current?.(map);
  }, []);

  return (
    <div className='leaflet-map-panel'>
      <MapContainer
        ref={mapRef}
        center={[center.latitude, center.longitude]}
        zoom={zoom}
        maxZoom={MAX_ZOOM}
        minZoom={MIN_ZOOM}
        maxBounds={MAX_BOUNDS}
      >
        <TileLayer
          url={`https://{s}.map.twipla.com/{z}/{x}/{y}.png?lang=${locale}`}
          attribution='&copy; <a href="http://osm.org/copyright" target="_blank">OpenStreetMap</a> contributors'
        />
        <CustomMarkerClusterGroup points={points} circleRadius={circleRadius} />
      </MapContainer>
    </div>
  );
};

export default LeafletMap;
